import { useState, useEffect, Fragment } from "react";
import { api } from "../api";
import { useConfirm } from "../components/ConfirmDialog.jsx";
import { useToast } from "../components/Toast.jsx";

const ROLES = { admin: "Администратор", employee: "Сотрудник", manager: "Менеджер" };

export default function AdminPage() {
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [form, setForm] = useState({ username: "", password: "", role: "employee", fullName: "" });
  const [staff, setStaff] = useState([]);
  const [nameEditId, setNameEditId] = useState(null);
  const [newFullName, setNewFullName] = useState("");
  const [nameSaving, setNameSaving] = useState(false);
  const [formError, setFormError] = useState("");
  const [saving, setSaving] = useState(false);
  const [passwordEditId, setPasswordEditId] = useState(null);
  const [newPassword, setNewPassword] = useState("");
  const [passwordError, setPasswordError] = useState("");
  const [passwordSaving, setPasswordSaving] = useState(false);
  const { confirm, ConfirmDialogElement } = useConfirm();
  const { showToast, ToastElement } = useToast();

  const load = () => {
    api.listUsers().then(setUsers).catch(() => {}).finally(() => setLoading(false));
  };
  useEffect(load, []);
  // Сотрудники YClients всех филиалов — подсказки для поля «ФИО» (на точке недоступно — вводится вручную).
  useEffect(() => {
    api.getYclientsAllStaff().then(r => setStaff(r?.staff || [])).catch(() => {});
  }, []);

  const staffIdFor = (fullName) => staff.find(s => s.name === fullName.trim())?.id ?? null;

  const handleSaveFullName = async (e) => {
    e.preventDefault();
    setNameSaving(true);
    try {
      const updated = await api.updateUser(nameEditId, { fullName: newFullName, yclientsStaffId: staffIdFor(newFullName) });
      setUsers(prev => prev.map(u => u.id === updated.id ? updated : u));
      setNameEditId(null);
    } catch (err) {
      showToast(err.response?.data?.error || "Ошибка");
    } finally {
      setNameSaving(false);
    }
  };

  const handleCreate = async (e) => {
    e.preventDefault();
    setFormError("");
    setSaving(true);
    try {
      const user = await api.createUser({ ...form, yclientsStaffId: staffIdFor(form.fullName) });
      setUsers(prev => [...prev, user]);
      setForm({ username: "", password: "", role: "employee", fullName: "" });
      setShowForm(false);
    } catch (err) {
      setFormError(err.response?.data?.error || "Ошибка");
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (id) => {
    if (!(await confirm("Удалить пользователя?", { danger: true }))) return;
    try {
      await api.deleteUser(id);
      setUsers(prev => prev.filter(u => u.id !== id));
    } catch (err) {
      showToast(err.response?.data?.error || "Ошибка удаления");
    }
  };

  const openPasswordEdit = (id) => {
    setPasswordEditId(id);
    setNewPassword("");
    setPasswordError("");
  };

  const closePasswordEdit = () => {
    setPasswordEditId(null);
    setNewPassword("");
    setPasswordError("");
  };

  const handleChangePassword = async (e) => {
    e.preventDefault();
    if (!newPassword.trim()) return;
    setPasswordError("");
    setPasswordSaving(true);
    try {
      await api.changePassword(passwordEditId, newPassword);
      showToast("Пароль изменён");
      closePasswordEdit();
    } catch (err) {
      setPasswordError(err.response?.data?.error || "Ошибка");
    } finally {
      setPasswordSaving(false);
    }
  };

  return (
    <div className="admin-page">
      <div className="admin-header">
        <h2 className="admin-title">Пользователи</h2>
        <button className="btn btn-primary" onClick={() => setShowForm(v => !v)}>
          {showForm ? "Отмена" : "+ Добавить"}
        </button>
      </div>

      <datalist id="yclients-staff">
        {staff.map(s => <option key={s.id} value={s.name}>{s.locations?.join(", ")}</option>)}
      </datalist>

      {showForm && (
        <form className="admin-form" onSubmit={handleCreate}>
          <input
            className="admin-input"
            placeholder="ФИО (из YClients или вручную)"
            list="yclients-staff"
            value={form.fullName}
            onChange={e => setForm(f => ({ ...f, fullName: e.target.value }))}
          />
          <input
            className="admin-input"
            placeholder="Логин"
            value={form.username}
            onChange={e => setForm(f => ({ ...f, username: e.target.value }))}
            required
          />
          <input
            className="admin-input"
            type="password"
            placeholder="Пароль"
            value={form.password}
            onChange={e => setForm(f => ({ ...f, password: e.target.value }))}
            required
          />
          <select
            className="admin-input"
            value={form.role}
            onChange={e => setForm(f => ({ ...f, role: e.target.value }))}
          >
            <option value="employee">Сотрудник</option>
            <option value="manager">Менеджер</option>
            <option value="admin">Администратор</option>
          </select>
          {formError && <div className="login-error">{formError}</div>}
          <button className="btn btn-primary" type="submit" disabled={saving}>
            {saving ? "..." : "Создать"}
          </button>
        </form>
      )}

      {loading ? (
        <div className="docs-empty">Загрузка...</div>
      ) : (
        <table className="admin-table">
          <thead>
            <tr>
              <th>Логин</th>
              <th>ФИО</th>
              <th>Роль</th>
              <th>Создан</th>
              <th></th>
            </tr>
          </thead>
          <tbody>
            {users.map(u => (
              <Fragment key={u.id}>
                <tr>
                  <td>{u.username}</td>
                  <td>
                    {u.fullName || <span style={{ color: "#bbb" }}>—</span>}
                    {u.yclientsStaffId && <span style={{ fontSize: 10, color: "#16a34a", marginLeft: 6 }}>YClients</span>}
                  </td>
                  <td>
                    <span className={`role-badge role-${u.role}`}>{ROLES[u.role] || u.role}</span>
                  </td>
                  <td style={{ fontSize: 12, color: "#888" }}>
                    {new Date(u.createdAt).toLocaleDateString("ru-RU")}
                  </td>
                  <td style={{ display: "flex", gap: 8 }}>
                    <button className="btn btn-ghost btn-sm" onClick={() => { setNameEditId(u.id); setNewFullName(u.fullName || ""); }}>
                      ФИО
                    </button>
                    <button className="btn btn-ghost btn-sm" onClick={() => openPasswordEdit(u.id)}>
                      Сменить пароль
                    </button>
                    <button className="btn btn-danger btn-sm" onClick={() => handleDelete(u.id)}>
                      Удалить
                    </button>
                  </td>
                </tr>
                {nameEditId === u.id && (
                  <tr>
                    <td colSpan={5}>
                      <form className="admin-form" onSubmit={handleSaveFullName} style={{ margin: "8px 0" }}>
                        <input
                          className="admin-input"
                          placeholder="ФИО (из YClients или вручную)"
                          list="yclients-staff"
                          value={newFullName}
                          onChange={e => setNewFullName(e.target.value)}
                          autoFocus
                        />
                        <button className="btn btn-primary" type="submit" disabled={nameSaving}>
                          {nameSaving ? "..." : "Сохранить"}
                        </button>
                        <button className="btn btn-ghost" type="button" onClick={() => setNameEditId(null)}>
                          Отмена
                        </button>
                      </form>
                    </td>
                  </tr>
                )}
                {passwordEditId === u.id && (
                  <tr>
                    <td colSpan={5}>
                      <form className="admin-form" onSubmit={handleChangePassword} style={{ margin: "8px 0" }}>
                        <input
                          className="admin-input"
                          type="password"
                          placeholder="Новый пароль"
                          value={newPassword}
                          onChange={e => setNewPassword(e.target.value)}
                          autoFocus
                          required
                        />
                        {passwordError && <div className="login-error">{passwordError}</div>}
                        <button className="btn btn-primary" type="submit" disabled={passwordSaving}>
                          {passwordSaving ? "..." : "Сохранить"}
                        </button>
                        <button className="btn btn-ghost" type="button" onClick={closePasswordEdit}>
                          Отмена
                        </button>
                      </form>
                    </td>
                  </tr>
                )}
              </Fragment>
            ))}
          </tbody>
        </table>
      )}
      <ConfirmDialogElement />
      <ToastElement />
    </div>
  );
}
